import { motion } from 'framer-motion';

const Credentials = () => {
  const credentials = [
    {
      icon: 'ri-graduation-cap-line',
      title: 'PhD in Problematic Studies',
      institution: 'Institute of Advanced Problem Recognition',
      year: '2003',
      description: 'Specialized in identifying issues that require no solutions but extensive over-analysis',
    },
    {
      icon: 'ri-award-line',
      title: 'Certified Problem Awareness Specialist',
      institution: 'Global Problem Identification Board',
      year: '2015',
      description: 'CPAS certification with highest honors in escalation and deliberate over-complication',
    },
    {
      icon: 'ri-medal-line',
      title: 'Master Problem Escalation Professional',
      institution: 'International Crisis Recognition Institute',
      year: '2015',
      description: 'MPEP designation for advanced problem magnification and bonus problem generation',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Fellow of Recursive Over-Thinking',
      institution: 'Academy of Unresolved Issues',
      year: '2018',
      description: 'Lifetime achievement in applying unnecessary complexity to straightforward situations',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Certified Issue Complexity Analyst',
      institution: 'Society for Complicated Matters',
      year: '2020',
      description: 'Expert in transforming simple, manageable issues into sprawling, multi-layered crises',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Distinguished Bonus Problem Award',
      institution: 'World Problem Conservation Foundation',
      year: '2023',
      description: 'Recognized for generating the most complimentary bonus problems per client engagement in recorded history',
    },
  ];

  const publications = [
    'The Art of Over-Complication: A 12-Volume Series (Abridged Edition: 9 Volumes)',
    'Why Solutions Are Overrated and Probably Creating New Problems: A Manifesto',
    'Advanced Techniques in Turning One Problem Into Several',
    'The Over-Thinker\'s Handbook: 4th Edition (The 3rd Edition Created Too Many Problems)',
    'Quarterly Journal of Unresolved Matters (Editor-in-Chief, Founding Member)',
  ];

  return (
    <section className="relative py-36 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/adbb51a4fa8050e3881485dc946b3b62.jpg"
          alt="Sunset landscape"
          width={1920}
          height={1080}
          loading="lazy"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black/85"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl font-black text-white mb-6">
            Credentials & Qualifications
          </h2>
          <p className="text-xl text-light-gray max-w-3xl mx-auto leading-relaxed">
            Dr. Thornwell holds more advanced degrees than any single person should reasonably need, 
            drawn from a wide spectrum of disciplines — each one contributing to his unparalleled 
            ability to over-think, over-complicate, and ensure your problems never run out.
          </p>
        </motion.div>

        {/* Credentials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {credentials.map((credential, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-charcoal/90 backdrop-blur-sm border-2 border-gold/30 rounded-lg p-8 h-full hover:border-gold transition-all duration-300 hover:bg-charcoal">
                <div className="w-16 h-16 rounded-full bg-gold/20 border-2 border-gold flex items-center justify-center mb-6 group-hover:bg-gold/30 transition-colors duration-300">
                  <i className={`${credential.icon} text-gold text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{credential.title}</h3>
                <p className="text-gold text-sm font-semibold mb-1">{credential.institution}</p>
                <p className="text-light-gray/70 text-sm mb-4">{credential.year}</p>
                <p className="text-light-gray text-sm leading-relaxed">{credential.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Publications Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-black/70 backdrop-blur-sm border-l-8 border-gold rounded-lg p-12"
        >
          <h3 className="text-3xl font-bold text-white mb-8">Published Works & Research</h3>
          <div className="space-y-4">
            {publications.map((publication, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start gap-4"
              >
                <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0"></div>
                <p className="text-lg text-light-gray">{publication}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-gold/30">
            <p className="text-light-gray italic text-center">
              "My research has been cited in over 3,200 academic papers on problem identification. 
              Notably, none have proposed any actual solutions. I consider this my greatest contribution to the field."
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Credentials;